import React, { useEffect, useState } from "react"
import { Button, Col, Container, Nav, NavItem, NavLink, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Spinners from "components/Shared/Spinners"

import {
  getAllCaterings,
  getCateringListRoaster,
} from "store/actions/cateringAction"
import { usePermissions } from "hooks"
import Datatable from "./component/Datatable"
import AddEditCatering from "./component/AddEditCatering"
import { getStationShiftDropdownList } from "store/actions/commonAction"
import { formatDateForAPI } from "components/Common/Utils"
import { Filter, FilterX } from "lucide-react"
import {
  resetPayload,
  setPayload,
  toggleFilterVisibility,
} from "store/reducers/cateringReducer"
import Filters from "components/Shared/Filters"

const Catering = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    loading,
    isLoading,
    isShowFilter,
    totalPages,
    totalCount,
    currentPage,
    caterings,
    payload,
  } = useSelector(state => state.Catering)

  const { work_shifts, stations, mealTypes } = useSelector(
    state => state.Common
  )
  const { permissions, showAll } = usePermissions("catering")

  const [trigger, setTrigger] = useState(false)
  const [addEditModal, setAddEditModal] = useState(false)
  const [activeTab, setActiveTab] = useState("to")

  const navItems = [
    { name: "Trial Operations", id: "to" },
    { name: "Roaster", id: "roaster" },
  ]

  useEffect(() => {
    getAllData()
  }, [trigger])

  useEffect(() => {
    dispatch(getStationShiftDropdownList())
  }, [])

  const getAllData = () => {
    const data = {
      ...payload,
      filters: {
        ...payload.filters,

        status:
          payload.filters.status === "all"
            ? null
            : payload.filters.status === "received"
            ? "1"
            : "0",
        date_range:
          payload.filters.date_range.length > 1
            ? {
                start_date: formatDateForAPI(payload.filters.date_range[0]),
                end_date: formatDateForAPI(payload.filters.date_range[1]),
              }
            : {},
      },
    }

    if (activeTab === "to") {
      dispatch(getAllCaterings(data))
    } else {
      dispatch(getCateringListRoaster(data))
    }
  }
  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const filters = {
    date_range: { type: "dateRange", label: t("Select Date") },
    status: {
      type: "select",
      label: t("Status"),
      options: [
        { value: "all", label: t("All") },
        { value: "received", label: t("Received") },
        { value: "not_received", label: t("Not Received") },
      ],
    },
    // is_trial_run: {
    //   type: "select",
    //   label: t("Trial Run"),
    //   options: [
    //     { value: "all", label: t("All") },
    //     { value: "yes", label: t("Yes") },
    //     { value: "no", label: t("No") },
    //   ],
    // },
    order_by: {
      type: "select",
      placeholder: t("Select Order"),
      label: t("Select Order"),
      options: [
        { value: 1, label: t("Ascending") },
        { value: -1, label: t("Descending") },
      ],
    },
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
    handleReset()
  }

  if (loading) return <Spinners />

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <div className="d-flex mb-4 justify-content-between align-items-center">
                <h4 className="card-title">{t("Catering")}</h4>
                <div className="d-flex gap-3 align-items-center justify-content-center">
                  {(showAll || permissions.includes("can_create")) && (
                    <div>
                      <button
                        className="main_btn"
                        onClick={() => {
                          setAddEditModal(true)
                          setActiveTab("to")
                        }}
                      >
                        {t("Add Trial Operation Catering")}
                      </button>
                    </div>
                  )}
                  {(showAll || permissions.includes("can_create")) && (
                    <div>
                      <button
                        className="main_btn"
                        onClick={() => {
                          setAddEditModal(true)
                          setActiveTab("roaster")
                        }}
                      >
                        {t("Add Roaster Catering")}
                      </button>
                    </div>
                  )}
                  <Button
                    color="primary"
                    onClick={() => dispatch(toggleFilterVisibility())}
                    title={t("Show/Hide Filter")}
                  >
                    {isShowFilter ? <FilterX /> : <Filter />}
                  </Button>
                </div>
              </div>
            </Col>
            <Nav tabs className="one_line_parent mb-3 mt-3">
              {navItems.map(item => (
                <NavItem key={item.id}>
                  <NavLink
                    className={activeTab === item.id ? "active" : ""}
                    onClick={() => toggleTab(item.id)}
                  >
                    {t(item.name)}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            {isShowFilter && (
              <Col xs="12">
                <Filters
                  filters={filters}
                  state={{
                    ...payload.filters,
                    order_by: payload.order_by,
                  }}
                  setState={data => {
                    const { order_by, ...rest } = data
                    dispatch(
                      setPayload({
                        order_by: Number(order_by),
                        filters: { ...payload.filters, ...rest },
                      })
                    )
                  }}
                  handleSearch={() => setTrigger(!trigger)}
                  handleReset={handleReset}
                />
              </Col>
            )}
            <Col xs="12">
              <Datatable
                data={caterings}
                currentPage={currentPage}
                loading={loading}
                onPageChange={onPageChange}
                totalCount={totalCount}
                totalPages={totalPages}
                showAll={showAll}
                permissions={permissions}
                isLoading={isLoading}
                work_shifts={work_shifts}
                stations={stations}
                mealTypes={mealTypes}
                trigger={() => setTrigger(!trigger)}
                payload={payload}
                showTialOperation={activeTab === "to"}
              />
            </Col>
          </Row>
        </Container>
      </div>
      {addEditModal && (
        <AddEditCatering
          showTrialOperation={activeTab === "to"}
          isOpen={addEditModal}
          toggle={() => setAddEditModal(false)}
          triger={() => setTrigger(!trigger)}
        />
      )}
    </>
  )
}

export default Catering
